import {
    LayoutDashboard,
    Trophy,
    LineChart,
    BookOpen,
    Target,
    Zap,
    FileStack,
    FileText,
    type LucideIcon,
    Dumbbell,
    Book,
    AlertCircle,
    User,
    HelpCircle,
    Shield,
    Activity,
    Users,
    PlusCircle,
    CheckSquare,
    BarChart,
    Swords,
    GraduationCap,
    Server,
    Video,
    PieChart,
    Bug,
    Coins,
    AppWindow,
    Database,
    Palette,
    CalendarRange,
} from "lucide-react"

/**
 * Single source of truth for dashboard navigation.
 *
 * Lifted out of `sidebar.tsx` so the header can name the current page from the
 * same data the sidebar links to — previously the header had no title at all,
 * and any new route would have needed its label written down twice.
 */

export const ROLE = {
    USER: 1,
    STUDENT: 2,
    MENTOR: 3,
    REVIEWER: 4,
    INSTRUCTOR: 5,
    ADMIN: 6,
    DEVELOPER: 7,
} as const

export interface SidebarItem {
    title: string
    href?: string
    icon: LucideIcon
    tag?: string
    hidden?: boolean
    disabled?: boolean
    /** Leaves the practice portal: opens in a new tab (the LMS, through one-click sign-in). */
    external?: boolean
    minRole?: number
    items?: SidebarItem[]
}

export interface SidebarSection {
    title: string
    items: SidebarItem[]
    /** Sections below this role are not rendered at all. */
    minRole?: number
}

export const dsatSections: SidebarSection[] = [
    {
        title: "Overview",
        items: [
            { title: "Home", href: "/dashboard", icon: LayoutDashboard },
            { title: "Leaderboard", href: "/dashboard/leaderboard", icon: Trophy },
            { title: "Performance", href: "/dashboard/performance", icon: LineChart },
            { title: "Universities", href: "/dashboard/universities", icon: GraduationCap },
            { title: "Community", href: "/dashboard/community", icon: Users },
        ],
    },
    {
        title: "Practice",
        items: [
            { title: "Study Planner", href: "/dashboard/planner", icon: CalendarRange, tag: "New" },
            { title: "Practice", href: "/dashboard/practice", icon: Target },
            { title: "Quiz", href: "/dashboard/quiz", icon: Zap },
            { title: "Mock Test", href: "/dashboard/mock-test", icon: FileText },
            { title: "Reading", href: "/dashboard/reading", icon: Book },
            { title: "Mistakes Log", href: "/dashboard/mistakes", icon: AlertCircle },
        ],
    },
    {
        title: "Learning",
        items: [
            { title: "My Courses", href: "/go-to-lms?to=/learn", icon: BookOpen, external: true },
            { title: "Video Library", href: "/dashboard/video-library", icon: Video, tag: "New" },
        ],
    },
    {
        title: "Social",
        items: [
            { title: "Connections", href: "/dashboard/connections", icon: Users },
            { title: "Mentors", href: "/dashboard/mentors", icon: Users },
        ],
    },
    {
        title: "Vocabulary",
        items: [
            { title: "Vocab", href: "/dashboard/vocab", icon: Dumbbell },
            { title: "Vocab Battle", href: "/dashboard/vocab/game", icon: Swords },
        ],
    },
    {
        title: "Account",
        items: [
            { title: "Profile", href: "/dashboard/profile", icon: User },
            { title: "Earn Particle", href: "/dashboard/earn-particle", icon: Coins, tag: "New" },
            { title: "Report Bug", href: "/dashboard/report-bug", icon: Bug },
            { title: "Support", href: "/dashboard/support", icon: HelpCircle, hidden: true },
        ],
    },
    {
        title: "Question Management",
        minRole: ROLE.REVIEWER,
        items: [
            { title: "Question Dashboard", href: "/admin/questions/dashboard", icon: BarChart },
            { title: "Add Question", href: "/admin/questions/add", icon: PlusCircle },
            { title: "Manage Question", href: "/admin/questions", icon: FileText },
            { title: "Reading Passages", href: "/admin/reading", icon: Book },
            { title: "Review Question", href: "/admin/review", icon: CheckSquare },
            { title: "Vocab Editor", href: "/admin/vocab", icon: Dumbbell, minRole: ROLE.REVIEWER },
            { title: "Skill Videos", href: "/admin/skill-videos", icon: Video },
        ],
    },
    {
        title: "Admin",
        minRole: ROLE.ADMIN,
        items: [
            { title: "Dashboard", href: "/admin/dashboard", icon: Shield },
            { title: "Analytics", href: "/admin/analytics", icon: PieChart },
            { title: "Student Monitor", href: "/admin/monitor", icon: Activity },
            { title: "API Monitor", href: "/admin/monitor/api", icon: Server },
            { title: "User Manage", href: "/admin/users", icon: Users },
            { title: "Mentor Manage", href: "/admin/mentors", icon: Users },
            { title: "Mock Tests", href: "/admin/mock-tests", icon: FileText },
            { title: "In-House Mock Tests", href: "/admin/inhouse-mock-tests", icon: FileStack },
            { title: "Video Library", href: "/admin/video-library", icon: Video },
            { title: "Universities", href: "/admin/universities", icon: GraduationCap },
            { title: "Finder Leads", href: "/admin/university-finder", icon: Target },
            { title: "Support Admin", href: "/admin/support", icon: HelpCircle, hidden: true },
        ],
    },
]

export const mentorSections: SidebarSection[] = [
    {
        title: "Mentor",
        // NOTE: the sidebar previously appended this for `role >= ROLE.USER`, i.e.
        // every signed-in student saw the mentor pages. Kept as-is to avoid changing
        // who can see what; raise to ROLE.MENTOR if that was unintended.
        minRole: ROLE.USER,
        items: [
            { title: "Mentor Dashboard", href: "/dashboard/mentor", icon: LayoutDashboard },
            { title: "Mentor Profile", href: "/dashboard/mentor/profile", icon: User },
        ],
    },
]

export const developerSections: SidebarSection[] = [
    {
        title: "Developer",
        minRole: ROLE.DEVELOPER,
        items: [
            { title: "Manage Bugs", href: "/admin/bugs", icon: Bug },
            { title: "Global References", href: "/admin/references", icon: BookOpen },
            { title: "Economy", href: "/admin/economy", icon: Coins },
            { title: "Membership", href: "/admin/membership", icon: Shield },
            { title: "Service Apps", href: "/admin/service-apps", icon: AppWindow },
            { title: "DB Dump", href: "/admin/developer/db-dump", icon: Database },
            { title: "Theme Studio", href: "/admin/theme", icon: Palette },
        ],
    },
]

export const EASY_MODE_ITEMS: SidebarItem[] = [
    { title: "Home", href: "/dashboard", icon: LayoutDashboard },
    { title: "Practice", href: "/dashboard/practice", icon: Target },
    { title: "Quiz", href: "/dashboard/quiz", icon: Zap },
    { title: "Mock Test", href: "/dashboard/mock-test", icon: FileText },
    { title: "Video Library", href: "/dashboard/video-library", icon: Video },
    { title: "Community", href: "/dashboard/community", icon: Users },
    { title: "Vocab", href: "/dashboard/vocab", icon: Dumbbell },
    { title: "Profile", href: "/dashboard/profile", icon: User },
]

/** Every navigable item, flattened — used to resolve a pathname to a label. */
const ALL_ITEMS: SidebarItem[] = [...dsatSections, ...mentorSections, ...developerSections]
    .flatMap((section) => section.items)
    .flatMap((item) => (item.items ? [item, ...item.items] : [item]))

/**
 * Names the current page for the header. Falls back to the longest matching
 * prefix so detail routes (`/dashboard/universities/42`) still resolve to their
 * parent's label, and finally to a title-cased last path segment.
 */
export function resolvePageTitle(pathname: string | null): string {
    if (!pathname) return "Dashboard"
    if (pathname === "/dashboard") return "Home"

    const exact = ALL_ITEMS.find((item) => item.href === pathname)
    if (exact) return exact.title

    const prefixMatch = ALL_ITEMS
        .filter((item) => item.href && item.href !== "/dashboard" && pathname.startsWith(`${item.href}/`))
        .sort((a, b) => (b.href!.length - a.href!.length))[0]
    if (prefixMatch) return prefixMatch.title

    const segment = pathname.split("/").filter(Boolean).pop() ?? "Dashboard"
    return segment
        .replace(/-/g, " ")
        .replace(/\b\w/g, (char) => char.toUpperCase())
}
